import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useProjektDetail, useProjektKennzahlen, useProjektAuftragswert } from "../hooks/usePlanung";
import DokumentLightbox, { type Dokument } from "../components/DokumentLightbox";
import ProjektKopf from "../components/projekt/ProjektKopf";
import ProjektMaterial from "../components/projekt/ProjektMaterial";
import { MaterialKopf } from "../components/projekt/AnfrageStand";
import { formatEur } from "../lib/planer";
import { useNachrichtenBezug } from "../components/nachrichten/NachrichtenKontext";
import { AusgabenSektion } from "../components/projekt/ProjektAusgaben";
import { DokumenteSektion } from "../components/projekt/ProjektDokumente";
import { PauschalenBlock } from "../components/projekt/ProjektPauschalen";
import { ProjektSprung, Sektion, VerlaufSektion, istTelefon } from "../components/projekt/ProjektSektion";
import { StundenBlock } from "../components/projekt/ProjektStunden";
import { TerminBlock } from "../components/projekt/ProjektTermine";
import ArbeitsplanSektion from "../components/arbeitsplan/ArbeitsplanSektion";
import { StammdatenDialog } from "../components/projekt/StammdatenDialog";
import { h1Zahl } from "../components/projekt/seiteHilfen";
import "../styles/projekte.css";
import "../styles/post.css";

/**
 * Projektseite. Oben der Projektkopf (Phase, nächster Schritt, Stand, Aufgaben,
 * Zahlen — `components/projekt/ProjektKopf.tsx`), darunter die Sektionen zum
 * Aufklappen: Clara, Kosten, Personal, Arbeitsplan, Termine, Material,
 * Dokumente, Verlauf. Die Sektionen liegen je in einer eigenen Datei unter
 * `components/projekt/` (Plan Projekt-Tracking, Etappe 2, 02.10.2026).
 */

export default function ProjektDetailPage() {
  const { id = "" } = useParams();
  const [suche, setSuche] = useSearchParams();

  const { data: d, isPending } = useProjektDetail(id);
  useNachrichtenBezug({ kundeId: d?.kunde_id, kundeName: d?.kunde_name, projektId: id, projektTitel: d?.title });
  const { data: kz } = useProjektKennzahlen(id);
  const { data: wert } = useProjektAuftragswert(id);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [dok, setDok] = useState<Dokument>(null);

  const netto = kz?.nettowert ?? 0;
  const personal = kz?.personalkosten_helfer ?? 0;
  const gewinn = kz?.gewinn ?? 0;
  const eur = (n: number) => formatEur(n, { round: true });
  const gewinnWeg = kz
    ? `Nettowert ${eur(netto)} − Material ${eur(kz.material_fuer_gewinn ?? 0)} − Verpflegung ${eur(kz.verpflegung_fuer_gewinn ?? 0)}`
      + ` − Personal Helfer ${eur(personal)}. Personal live aus den Einsätzen, nicht bestätigte zählen mit.`
    : undefined;

  if (isPending) return <div className="projekte-page"><div className="vg-empty">Lade Projekt…</div></div>;

  return (
    <div className="projekte-page">
      <ProjektKopf
        projektId={id} d={d} wert={wert} gewinn={gewinn} netto={netto}
        gewinnWeg={gewinnWeg}
        stundenlohn={kz ? { betrag: kz.stundenlohn ?? 0, inhaberStunden: kz.owner_stunden ?? 0, helferStunden: kz.helfer_stunden ?? 0 } : undefined}
        onStammdaten={() => setBearbeiten(true)}
      >
        {suche.get("neu") === "leed" && !istTelefon() && (
          /* Frisch aus einem Leed entstanden: der nächste Schritt ist fast immer das Angebot. */
          <div className="vg-banner vg-banner-weiter">
            <span>Projekt angelegt, der Schriftwechsel hängt schon dran.</span>
            <Link className="vg-btn vg-btn-primary" to={`/rechnungen/neu?kind=quote&projekt=${id}${d?.kunde_id ? `&kunde=${d.kunde_id}` : ""}`}>
              Angebot erstellen
            </Link>
            <button type="button" className="vg-btn vg-btn-ghost" onClick={() => { suche.delete("neu"); setSuche(suche, { replace: true }); }}>
              Später
            </button>
          </div>
        )}
      </ProjektKopf>

      <ProjektSprung />

      {/* Erst alle Kosten untereinander, dann die Termine, zuletzt die
          Dokumente — von „was kostet es" über „wann passiert es" zu „was liegt
          dazu vor". Vorher stand die Dokumentenkarte zwischen Gewinn und
          Kosten und riss den Kostenblock auseinander. */}
      <AusgabenSektion projektId={id} typ="material" titel="Materialausgaben" onZeigen={setDok} anker="kosten" />
      <AusgabenSektion projektId={id} typ="verpflegung" titel="Verpflegung" onZeigen={setDok} />

      <Sektion
        titel="Personalkosten"
        kopfRechts={
          /* Inhaberstunden gehoeren hierher, auch wenn sie kein Aufwand sind:
             sonst liest sich der Kopf, als haette am Projekt nur das Team
             gearbeitet. Die Summe bleibt die der Helferkosten. */
          <>
            {h1Zahl(kz?.helfer_stunden ?? 0)} h Helfer
            {" · "}{h1Zahl(kz?.owner_stunden ?? 0)} h Inhaber
            {" · Σ "}{formatEur(personal, { round: true })}
          </>
        }
      >
        <PauschalenBlock projektId={id} />
        <StundenBlock projektId={id} />
      </Sektion>

      {/* Kein "Personalausgaben-Belege" mehr (entfernt 17.08.2026).
          Der Abschnitt zeigte `projekt_ausgabe` mit `ausgabe_typ='helfer'` —
          davon gibt es im gesamten Bestand keine einzige Zeile, er stand also
          immer auf "0 Posten · Σ 0 €". Was ein Helfer kostet, steht darueber
          unter Personalkosten (Stunden + Pauschalen); seine Rechnung liegt im
          Belegwesen, nicht am Projekt. Zwei Orte fuer dieselbe Frage laden zum
          Doppelzaehlen ein. Die Variante bleibt im Backend erhalten, falls die
          Belegzuordnung spaeter doch am Projekt gebraucht wird. */}

      {/* Der Arbeitsplan (Etappe 6): Tage ohne Datum, Aufteilung aus dem Angebot,
          in den Kalender am Stück oder einzeln. Nicht bei Pflege — dort plant der Vertrag. */}
      {d?.projektart !== "pflege" && (
        <Sektion titel="Arbeitsplan" anker="plan" standardOffen={!istTelefon()}>
          <ArbeitsplanSektion projektId={id} />
        </Sektion>
      )}

      <Sektion titel="Termine" anker="termine">
        <TerminBlock projektId={id} />
      </Sektion>

      {/* Materialien aus dem Angebot und ihre Lieferanten — anrufen, anschreiben,
          anfragen (Benjamin 25.09.2026, docs/system/KONZEPT-PROJEKT-LIEFERANTEN.md).
          Im Kopf, was auf Antwort wartet — die Sektion ist zugeklappt. */}
      <Sektion titel="Material & Lieferanten" anker="material" kopfRechts={<MaterialKopf projektId={id} />}>
        <ProjektMaterial projektId={id} onZeigen={setDok} />
      </Sektion>

      <DokumenteSektion projektId={id} onZeigen={setDok} />

      <VerlaufSektion projektId={id} />

      <DokumentLightbox dok={dok} onClose={() => setDok(null)} />
      {bearbeiten && <StammdatenDialog projektId={id} onClose={() => setBearbeiten(false)} />}
    </div>
  );
}
